import React, { createContext, useContext, useState, useEffect } from 'react';
import { CustomerReview } from '../types/store';
import { CUSTOMER_REVIEWS as DEFAULT_REVIEWS } from '../data/mockData';
import {
  subscribeReviewsDb,
  saveReviewDb,
  deleteReviewDb,
  deleteAllDummyReviewsDb,
  deleteAllReviewsDb,
  resetReviewsDb,
  getLocalCachedReviews,
  isDummyReview,
} from '../lib/firebase';

interface ReviewsContextType {
  reviews: CustomerReview[];
  isLoading: boolean;
  error: string | null;
  addReview: (reviewData: Omit<CustomerReview, 'id'> | CustomerReview) => Promise<void>;
  updateReview: (review: CustomerReview) => Promise<void>;
  deleteReview: (reviewId: string) => Promise<void>;
  deleteAllDummyReviews: () => Promise<void>;
  deleteAllReviews: () => Promise<void>;
  resetReviews: () => Promise<void>;
  likeReview: (reviewId: string) => void;
}

const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined);

export const ReviewsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reviews, setReviews] = useState<CustomerReview[]>(getLocalCachedReviews());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Real-time synchronization with Firestore and LocalStorage
    const unsubscribe = subscribeReviewsDb(
      (updatedReviews) => {
        setReviews(updatedReviews || []);
        setIsLoading(false);
      },
      (err) => {
        console.warn('Firestore reviews sync notice:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const addReview = async (reviewData: Omit<CustomerReview, 'id'> | CustomerReview) => {
    const id = 'id' in reviewData && reviewData.id ? reviewData.id : `rev-${Date.now()}`;
    const newReview: CustomerReview = {
      ...reviewData,
      id,
      rating: Number(reviewData.rating) || 5,
      date: reviewData.date || 'Just now',
      verifiedBuyer: reviewData.verifiedBuyer !== false,
      likesCount: reviewData.likesCount || 0,
    };

    // Optimistic UI update
    setReviews((prev) => [newReview, ...prev.filter((r) => r.id !== id)]);
    await saveReviewDb(newReview);
  };

  const updateReview = async (review: CustomerReview) => {
    // Optimistic UI update
    setReviews((prev) => prev.map((r) => (r.id === review.id ? review : r)));
    await saveReviewDb(review);
  };

  const deleteReview = async (reviewId: string) => {
    // Optimistic UI update - immediate deletion
    setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    await deleteReviewDb(reviewId);
  };

  const deleteAllDummyReviews = async () => {
    // Optimistic UI update - immediately remove all dummy reviews
    setReviews((prev) => prev.filter((r) => !isDummyReview(r.id)));
    await deleteAllDummyReviewsDb();
  };

  const deleteAllReviews = async () => {
    // Optimistic UI update
    setReviews([]);
    await deleteAllReviewsDb();
  };

  const resetReviews = async () => {
    setReviews(DEFAULT_REVIEWS);
    await resetReviewsDb();
  };

  const likeReview = (reviewId: string) => {
    setReviews((prev) =>
      prev.map((r) => {
        if (r.id === reviewId) {
          const updated = { ...r, likesCount: (r.likesCount || 0) + 1 };
          saveReviewDb(updated).catch(console.error);
          return updated;
        }
        return r;
      })
    );
  };

  return (
    <ReviewsContext.Provider
      value={{
        reviews,
        isLoading,
        error,
        addReview,
        updateReview,
        deleteReview,
        deleteAllDummyReviews,
        deleteAllReviews,
        resetReviews,
        likeReview,
      }}
    >
      {children}
    </ReviewsContext.Provider>
  );
};

export const useReviews = () => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviews must be used within a ReviewsProvider');
  }
  return context;
};

